// Pure "what's the ONE next step?" derivation for the project Overview tab
// (ProjectOverviewTab → NextStepBlock).
//
// CEO 2026-07-29 (first real client in the system): on a fresh project's
// Overview tab there was no way forward — "I'm missing buttons to Create a
// Presentation, Create an Estimate." The creates existed only inside each tab's
// empty state (round-43), and the muted empty nav items read as disabled. This
// module gives the Overview a single, stage-aware pointer to the next action so
// the rep is never stranded on the landing tab.
//
// Kept dependency-free of React and of `@/` aliases (only @merchd/shared, a
// workspace package) so the qa-overview-next-action gate can `import()` and
// EXECUTE deriveProjectNextStep() against fixtures — a source lint can't prove
// the state machine offers the right single step; running it can.
//
// SINGLE SOURCE (round-49 rule — no forked stage logic): whether a create step
// is legitimate at a given stage is decided ONLY by the shared predicates
// canCreatePresentationAtStage / canCreateEstimateAtStage — the SAME gate the
// server rule engine (getAvailableActions) and the tab empty-states consume. The
// gate executes an equivalence between what this module offers and those
// predicates across every stage, so the Overview can't drift from the flow.
//
// This is a POINTER, not a second dashboard: at most one primary + one secondary
// per state (house one-primary rule). Creation logic is NOT duplicated here — the
// create_presentation step fires the shared useCreatePresentation mutation, and
// create_estimate navigates to the Estimate tab's own create affordance
// (?new=1), exactly the in-context machinery round-43 built.
import {
  canCreatePresentationAtStage,
  canCreateEstimateAtStage,
} from "@merchd/shared";

/** The single next step kind the rep should take from the Overview tab. */
export type NextStepKind =
  | "create_presentation" // fresh project → fire the shared create mutation
  | "create_estimate" // skip-ahead / from-presentation → Estimate tab (?new=1)
  | "create_sales_order" // straight to a quote → Quote tab (?new=1)
  | "view_presentation" // a presentation exists, no order yet → work it forward
  | "view_estimate" // an estimate is in flight → open it (send / convert)
  | "view_sales_order" // a committed SO exists → open it (production lives there)
  | "view_invoice"; // an invoice exists → open it

export interface NextStepAction {
  kind: NextStepKind;
  label: string;
  /**
   * In-project route for the navigation steps (create_estimate + every view_*).
   * `null` ONLY for create_presentation, which is fired via the shared
   * useCreatePresentation mutation (POST /presentations → new builder), not a
   * navigation — so creation logic never gets duplicated into the Overview.
   */
  href: string | null;
}

/** The derivation input — stage + the cancellation-excluding stage-node doc
 *  counts the tabs already read (data.stages.find(…)?.count), NOT legacy
 *  counts.* which include cancelled rows. */
export interface ProjectNextStepInput {
  projectId: string;
  statusStage: string;
  presentationCount: number;
  estimateCount: number;
  salesOrderCount: number;
  invoiceCount: number;
}

export interface ProjectNextStep {
  /** The single most-important forward action. `null` on terminal projects and
   *  on the (rare) degenerate stage/no-doc cell where no create is legitimate. */
  primary: NextStepAction | null;
  /** At most one secondary. */
  secondary: NextStepAction | null;
  /** Fresh project only: "New quote", offered with the other two starts
   *  (Stephanie 2026-10-12 — a job can go straight to a quote). */
  tertiary: NextStepAction | null;
  /** Quiet, non-actionable line for terminal projects (closed / lost) — the
   *  header carries Reopen, so the Overview must NOT offer a stale create. */
  terminalNote: string | null;
}

function createPresentationAction(): NextStepAction {
  // Label MUST match the Presentation tab's create button ("New presentation",
  // ProjectPresentationTab.tsx) — the same action can't wear two names (the
  // Add-Product label lesson). qa-overview-next-action asserts this parity.
  return { kind: "create_presentation", label: "New presentation", href: null };
}
function createEstimateAction(projectId: string): NextStepAction {
  // Label MUST match the Estimate tab's toolbar create button ("New estimate",
  // ProjectEstimateTab.tsx). The tab's create form opens directly on ?new=1 (the
  // same escape hatch OrderDetailPage's New-estimate affordance uses) —
  // navigating there is the acceptable "navigate-to-tab-with-create-focused"
  // path, no duplicated create logic. Parity asserted by qa-overview-next-action.
  return {
    kind: "create_estimate",
    label: "New quote",
    href: `/projects/${projectId}/quotes?new=1`,
  };
}
function viewPresentationAction(projectId: string): NextStepAction {
  return {
    kind: "view_presentation",
    label: "Open presentation",
    href: `/projects/${projectId}/presentation`,
  };
}
function viewEstimateAction(projectId: string): NextStepAction {
  return {
    kind: "view_estimate",
    label: "Open quote",
    href: `/projects/${projectId}/quotes`,
  };
}
function viewSalesOrderAction(projectId: string): NextStepAction {
  return {
    kind: "view_sales_order",
    label: "Open quote",
    href: `/projects/${projectId}/quotes`,
  };
}
function viewInvoiceAction(projectId: string): NextStepAction {
  return {
    kind: "view_invoice",
    label: "Open invoice",
    href: `/projects/${projectId}/invoice`,
  };
}

/**
 * Derive the ONE next step from the project's stage + stage-node doc counts.
 *
 * Precedence, top to bottom:
 *   terminal (closed/lost) → quiet note, NEVER a create
 *   invoice exists        → open the invoice
 *   quote exists    → open the quote (production/POs live there)
 *   estimate exists       → open the estimate (send for approval / convert)
 *   presentation exists   → open the presentation; secondary "Create estimate"
 *                            ONLY where the shared gate allows it
 *   nothing yet (fresh)   → the CEO-reported state: offer the creates the SHARED
 *                            gate allows, in flow order (presentation, estimate).
 *                            Skip-ahead to an estimate is legitimate (the
 *                            document-flow spec: a presentation is optional).
 *
 * Create offers ALWAYS pass through canCreatePresentationAtStage /
 * canCreateEstimateAtStage — the single source — so terminal and post-sale
 * stages can never surface a stale create.
 */
export function deriveProjectNextStep(
  input: ProjectNextStepInput,
): ProjectNextStep {
  const { projectId, statusStage } = input;
  const canPresentation = canCreatePresentationAtStage(statusStage);
  const canEstimate = canCreateEstimateAtStage(statusStage);

  // Terminal — the header owns Reopen; the Overview must not offer a create.
  if (statusStage === "closed" || statusStage === "lost") {
    return {
      primary: null,
      secondary: null,
      tertiary: null,
      terminalNote:
        statusStage === "lost"
          ? "This project was marked lost. Reopen it from the header to pick it back up."
          : "This project is closed. Reopen it from the header to keep working.",
    };
  }

  // Forward-flow: point at the furthest-along document first.
  if (input.invoiceCount > 0) {
    return { primary: viewInvoiceAction(projectId), secondary: null, tertiary: null, terminalNote: null };
  }
  if (input.salesOrderCount > 0) {
    return {
      primary: viewSalesOrderAction(projectId),
      secondary: null,
      tertiary: null,
      terminalNote: null,
    };
  }
  if (input.estimateCount > 0) {
    return {
      primary: viewEstimateAction(projectId),
      secondary: null,
      tertiary: null,
      terminalNote: null,
    };
  }
  if (input.presentationCount > 0) {
    // A presentation exists but no order yet — the forward move is to work it
    // into an estimate. Offer a blank estimate as the secondary ONLY where the
    // shared gate allows (single source), never a second presentation.
    return {
      primary: viewPresentationAction(projectId),
      secondary: canEstimate ? createEstimateAction(projectId) : null,
      tertiary: null,
      terminalNote: null,
    };
  }

  // Fresh project — no documents yet. The exact state the CEO hit. Offer the
  // creates the SHARED gate permits, in flow order. When a stage legitimately
  // allows only the estimate (e.g. a doc-less project sitting at the estimate
  // stage), create_presentation is correctly withheld — the gate says so.
  const creates: NextStepAction[] = [];
  if (canPresentation) creates.push(createPresentationAction());
  // One "New quote" (CEO 2026-10-15: Estimate + Sales Order are one Quote).
  if (canEstimate) creates.push(createEstimateAction(projectId));
  return {
    primary: creates[0] ?? null,
    secondary: creates[1] ?? null,
    tertiary: creates[2] ?? null,
    terminalNote: null,
  };
}
